import React, { useState, useCallback, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import dayjs from "dayjs";
import classNames from "classnames";
import forecaSymbols from "assets/images/foreca/forecaSymbols";
import styles from "./ForecastDataGrid.module.scss";
import { grey } from "@mui/material/colors";
import {
  Box,
  IconButton,
  Link,
  Tooltip,
  Typography,
  Table,
  TableContainer,
} from "@mui/material";
import { ProtectedPaths } from "routes";
import { ProductionForecastNew } from "api/models/ProductionForecast";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import LoadingButton from "@mui/lab/LoadingButton";
import { updateProductionForecast } from "api/resources/productionForecasts";
import { formatNumberByThousands } from "helpers/numbers";
import { saveProductionParkDefaultWeights } from "api/resources/productionPark";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import WindPowerIcon from "@mui/icons-material/WindPower";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import { get15MinStartTimesForDate } from "helpers/timeIntervals";

interface ProductionForecastTableProps {
  productionForecast: ProductionForecastNew;
  setSnackbar: any;
  forecastValues: any;
  setForecastValues: any;
  onCalculated: any;
  calculatedValues: any;
  filters: any;
  productionParks: any;
}

const weightTypes = {
  AIOLOS: "solar",
  AIOLOS_WIND: "wind",
  METEOLOGICA: "solar",
  METEOLOGICA_WIND: "wind",
  FORECA: "solar",
  CONWX: "solar",
};

const getProviderType = (
  providerKey: string,
  parkType?: string,
): "solar" | "wind" => {
  // If provider has explicit _WIND suffix, it's always wind
  if (providerKey.endsWith("_WIND")) {
    return "wind";
  }

  // For FORECA, always solar
  if (providerKey === "FORECA") {
    return "solar";
  }

  // For non-suffixed providers (AIOLOS, METEOLOGICA, CONWX),
  // determine type based on park type
  if (parkType === "WIND") {
    return "wind";
  }

  // Default to solar for SOLAR and HYBRID parks
  return "solar";
};

const ProductionForecastTable: React.FC<ProductionForecastTableProps> = ({
  productionForecast,
  setSnackbar,
  onCalculated,
  productionParks,
}) => {
  const [tableMatrix, setTableMatrix] = useState([]);
  const [weights, setWeights] = useState({
    AIOLOS: {
      type: "solar",
      value: 0,
    },
    AIOLOS_WIND: {
      type: "wind",
      value: 0,
    },
    METEOLOGICA: {
      type: "solar",
      value: 0,
    },
    METEOLOGICA_WIND: {
      type: "wind",
      value: 0,
    },
    FORECA: {
      type: "solar",
      value: 0,
    },
    CONWX: {
      type: "solar",
      value: 0,
    },
  });
  const [weightError, setWeightError] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [weightsChanged, setWeightsChanged] = useState(false);

  const periodStarts = get15MinStartTimesForDate(
    productionForecast.forecastDate,
  );

  const forecastTypes = {
    AIOLOS: "Aiolos",
    AIOLOS_WIND: "Aiolos (wind)",
    METEOLOGICA: "Meteologica",
    METEOLOGICA_WIND: "Meteologica (wind)",
    CONWX: "ConWX",
    FORECA: "Foreca",
  };

  const availableForecastTypes = useMemo(() => {
    if (!productionForecast) return [];

    const types = new Set<string>();
    productionForecast.intervals.forEach((interval) => {
      interval.values.forEach((value) => {
        if (value.type !== "FORECAST") {
          types.add(value.type);
        }
      });
    });
    return Array.from(types);
  }, [productionForecast]);

  // Extract production park type and maxPowerOfNetworks from productionParks data
  const productionParkType = useMemo(() => {
    if (!productionParks || !productionForecast?.productionPark?.id) {
      return undefined;
    }

    // productionParks is an object with a content array
    const parksArray = productionParks.content;
    if (!Array.isArray(parksArray)) {
      return undefined;
    }

    const currentPark = parksArray.find(
      (park: any) => park.id === productionForecast.productionPark.id,
    );
    return currentPark?.type;
  }, [productionParks, productionForecast?.productionPark?.id]);

  const maxPowerOfNetworks = useMemo(() => {
    if (!productionParks || !productionForecast?.productionPark?.id) {
      return undefined;
    }

    const parksArray = productionParks.content;
    if (!Array.isArray(parksArray)) {
      return undefined;
    }

    const currentPark = parksArray.find(
      (park: any) => park.id === productionForecast.productionPark.id,
    );
    return currentPark?.maxPowerOfNetworks;
  }, [productionParks, productionForecast?.productionPark?.id]);

  useEffect(() => {
    const tableData = [];
    const forecastValuesCopy = [];

    productionForecast?.intervals.map((interval, intervalIndex) => {
      const mappedValues = interval.values.map((value) => ({
        id: value.id,
        type: value.type,
        value: value.value,
        isSelected: value.isSelected,
      }));

      // Externally adjusted intervals: skip recalculation, use the
      // backend-provided FORECAST value as-is.
      if (interval.hasAdjustments) {
        const forecastEntry = interval.values.find(
          (v) => v.type === "FORECAST",
        );
        const adjustedForecast = Number(forecastEntry?.value ?? 0);

        tableData.push({
          id: interval.id,
          startTime: dayjs(interval.startTime).format("HH:mm"),
          values: mappedValues,
          correction: interval.correction,
          forecast: adjustedForecast,
          hasAdjustments: true,
        });

        forecastValuesCopy[intervalIndex] = adjustedForecast;
        return;
      }

      let solarSum = 0;
      let windSum = 0;

      const isSelectedCount = interval.values.filter(
        (v) => v.isSelected && availableForecastTypes.includes(v.type),
      ).length;

      interval.values.forEach((value) => {
        if (availableForecastTypes.includes(value.type)) {
          const providerType =
            weights[value.type as keyof typeof weights]?.type;

          let contributionValue = 0;

          if (!value.isSelected || value.value === 0) {
            contributionValue = 0;
          } else if (value.isSelected && isSelectedCount === 1) {
            contributionValue = value.value;
          } else {
            contributionValue =
              (weights[value.type as keyof typeof weights].value / 100) *
              value.value;
          }

          // Add to appropriate sum based on provider type
          if (providerType === "solar") {
            solarSum += contributionValue;
          } else if (providerType === "wind") {
            windSum += contributionValue;
          }
        }
      });

      // Calculate total before network cap
      let forecastSum = solarSum + windSum;

      // Apply maxPowerOfNetworks cap for hybrid parks
      if (
        productionParkType === "HYBRID" &&
        maxPowerOfNetworks &&
        forecastSum > maxPowerOfNetworks
      ) {
        forecastSum = maxPowerOfNetworks;
      }

      tableData.push({
        id: interval.id,
        startTime: dayjs(interval.startTime).format("HH:mm"),
        values: mappedValues,
        correction: interval.correction,
        forecast: forecastSum + interval.correction,
        hasAdjustments: false,
      });

      forecastValuesCopy[intervalIndex] = forecastSum + interval.correction;
    });

    setTableMatrix(tableData);
    onCalculated(productionForecast.id, forecastValuesCopy);
  }, [
    availableForecastTypes,
    productionForecast,
    weights,
    productionParkType,
    maxPowerOfNetworks,
  ]);

  useEffect(() => {
    if (!productionForecast?.weights) return;

    const newWeights = {};
    productionForecast.weights.forEach((weight) => {
      newWeights[weight.type] = {
        type: weightTypes[weight.type],
        value: weight.value * 100,
      };
    });

    setWeights((currentWeights) => ({
      ...currentWeights,
      ...newWeights,
    }));
  }, [productionForecast?.weights]);

  const debouncedCalculateForecastValues = useCallback(
    debounce(() => {
      const forecastValuesCopy: number[] = [];
      const tableDataCopy = [...tableMatrix];

      tableMatrix.forEach((interval, intervalIndex) => {
        // Externally adjusted intervals are not recalculated — keep the
        // backend-provided forecast value regardless of frontend changes.
        if (interval.hasAdjustments) {
          forecastValuesCopy[intervalIndex] = interval.forecast;
          return;
        }

        let solarSum = 0;
        let windSum = 0;

        const isSelectedCount = interval.values.filter(
          (v) => v.isSelected && availableForecastTypes.includes(v.type),
        ).length;

        interval.values.forEach((value) => {
          if (availableForecastTypes.includes(value.type)) {
            const providerType =
              weights[value.type as keyof typeof weights]?.type;

            let contributionValue = 0;

            if (!value.isSelected || value.value === 0) {
              contributionValue = 0;
            } else if (value.isSelected && isSelectedCount === 1) {
              contributionValue = value.value;
            } else {
              contributionValue =
                (weights[value.type as keyof typeof weights].value / 100) *
                value.value;
            }

            // Add to appropriate sum based on provider type
            if (providerType === "solar") {
              solarSum += contributionValue;
            } else if (providerType === "wind") {
              windSum += contributionValue;
            }
          }
        });

        // Calculate total before network cap
        let forecastSum = solarSum + windSum;

        // Apply maxPowerOfNetworks cap for hybrid parks
        if (
          productionParkType === "HYBRID" &&
          maxPowerOfNetworks &&
          forecastSum > maxPowerOfNetworks
        ) {
          forecastSum = maxPowerOfNetworks;
        }

        forecastValuesCopy[intervalIndex] =
          forecastSum + Number(tableMatrix[intervalIndex]?.correction);
        tableDataCopy[intervalIndex].forecast =
          forecastSum + Number(tableMatrix[intervalIndex]?.correction);
      });

      setTableMatrix(tableDataCopy);
      onCalculated(productionForecast.id, forecastValuesCopy);
    }, 1000), // 300ms debounce
    [
      tableMatrix,
      weights,
      availableForecastTypes,
      productionParkType,
      maxPowerOfNetworks,
    ],
  );

  const handleCellClick = (rowIndex: number, type: string) => {
    const tableMatrixCopy = [...tableMatrix];

    tableMatrixCopy[rowIndex].values.find((v) => v.type === type).isSelected =
      !tableMatrixCopy[rowIndex].values.find((v) => v.type === type).isSelected;

    setTableMatrix(tableMatrixCopy);
    setValuesChanged(true);
    debouncedCalculateForecastValues();
  };

  const handleSetCorrection = (rowIndex: number, correction: number) => {
    const tableMatrixCopy = [...tableMatrix];

    tableMatrixCopy[rowIndex].correction = correction;

    setTableMatrix(tableMatrixCopy);

    debouncedCalculateForecastValues();

    setValuesChanged(true);
  };

  const handleSetWeight = (key: string, newValue: string) => {
    const weightsCopy = { ...weights };

    weightsCopy[key as keyof typeof weightsCopy].value =
      newValue === "" ? "" : Number(newValue);

    setWeights(weightsCopy);
    setValuesChanged(true);
    setWeightsChanged(true);

    // Check which provider types are available
    const hasSolarProviders = availableForecastTypes.some((type) =>
      Object.entries(weightsCopy).some(
        ([provider, w]) => provider === type && w.type === "solar",
      ),
    );
    const hasWindProviders = availableForecastTypes.some((type) =>
      Object.entries(weightsCopy).some(
        ([provider, w]) => provider === type && w.type === "wind",
      ),
    );

    // Sum solar and wind groups
    const solarTotal = Object.entries(weightsCopy)
      .filter(([provider, w]) =>
        w.type === "solar" && availableForecastTypes.includes(provider)
      )
      .reduce((sum, [, w]) => sum + Number(w.value || 0), 0);

    const windTotal = Object.entries(weightsCopy)
      .filter(([provider, w]) =>
        w.type === "wind" && availableForecastTypes.includes(provider)
      )
      .reduce((sum, [, w]) => sum + Number(w.value || 0), 0);

    // Trigger error only if available providers don't sum to 100
    const solarError = hasSolarProviders && solarTotal !== 100;
    const windError = hasWindProviders && windTotal !== 100;

    if (solarError || windError) {
      setWeightError(true);
    } else {
      setWeightError(false);
      debouncedCalculateForecastValues();
    }
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const handleMouseDown = (event: any) => {
    if (event.button === 0) {
      setIsMouseDown(true);
    }
  };

  const handleSaveForecast = () => {
    setSaving(true);

    const weightsData = Object.entries(weights)
      .filter(([type]) => availableForecastTypes.includes(type))
      .map(([provider, obj]) => ({
        provider,
        weight: Number(obj.value) / 100,
      }));

    const intervalCorrections = tableMatrix.map((interval) => {
      return {
        intervalId: interval.id,
        correction: interval.correction,
      };
    });

    const intervalValueSelections = tableMatrix.flatMap((interval) =>
      interval.values.map((value) => ({
        intervalValueId: value.id,
        isSelected: value.isSelected,
      })),
    );

    const postData = {
      weights: weightsData,
      intervalCorrections,
      intervalValueSelections,
    };

    updateProductionForecast(productionForecast.id, postData)
      .then(() => {
        setSaving(false);
        setValuesChanged(false);
        // setSnackbar({
        //   type: "success",
        //   message: "Forecast saved",
        // });
      })
      .catch((error) => {
        setSaving(false);
        console.error({ error });
        setSnackbar({
          type: "success",
          message: "Error: " + error.message,
        });
      });
  };

  const handleSaveDefaultWeights = () => {
    const weightsTemp: { type: string; weight: number }[] = [];

    Object.entries(weights).forEach(([key, obj]) => {
      weightsTemp.push({
        type: key,
        weight: Number(obj.value) / 100, // <-- FIXED
      });
    });

    const postData = {
      weights: weightsTemp,
    };

    saveProductionParkDefaultWeights(
      productionForecast.productionPark.id,
      postData,
    )
      .then(() => {
        setWeightsChanged(false);
      })
      .catch((error) => console.error(error));
  };

  const preferredOrder = [
    "FORECA",
    "AIOLOS",
    "AIOLOS_WIND",
    "CONWX",
    "METEOLOGICA",
    "METEOLOGICA_WIND",
  ];

  if (!productionForecast) return null;

  return (
    <div
      className={styles.forecastDataGrid}
      style={{
        display: "flex",
        flexDirection: "row",
        overflowY: "unset",
        minWidth: "fit-content",
      }}
    >
      <TableContainer>
        <Table
          className={classNames(styles.table)}
          style={{
            border: "solid",
            borderWidth: 1,
            borderTop: 0,
            borderBottom: 0,
            borderRight: 1,
            borderColor: grey[400],
            borderCollapse: "collapse",
            userSelect: "none",
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          stickyHeader
        >
          <thead>
            <tr style={{ background: grey[200] }}>
              <th colSpan={100} style={{ textAlign: "left" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  minHeight="34px"
                >
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {productionParkType === "SOLAR" && (
                      <WbSunnyIcon
                        fontSize="small"
                        sx={{ mr: 0.5, color: grey[700] }}
                      />
                    )}
                    {productionParkType === "WIND" && (
                      <WindPowerIcon
                        fontSize="small"
                        sx={{ mr: 0.5, color: grey[700] }}
                      />
                    )}
                    {productionParkType === "HYBRID" && (
                      <>
                        <WbSunnyIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: grey[700] }}
                        />
                        <WindPowerIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: grey[700] }}
                        />
                      </>
                    )}
                    <Link
                      href={`/${ProtectedPaths.EditProductionParks}/${productionForecast.productionPark.id}`}
                    >
                      {productionForecast.productionPark.name}
                    </Link>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ ml: 1 }}
                    >
                      {productionForecast.productionPark.code}
                    </Typography>
                  </Box>
                  <Box>
                    {weightsChanged && (
                      <Tooltip title="Save default weights">
                        <IconButton
                          size="small"
                          onClick={handleSaveDefaultWeights}
                          disabled={weightError}
                          color="primary"
                          sx={{ ml: 1 }}
                        >
                          <SaveAltIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {valuesChanged && (
                      <LoadingButton
                        variant="contained"
                        size="small"
                        loadingIndicator="Saving..."
                        loading={saving}
                        disabled={weightError}
                        sx={{ ml: 1 }}
                        onClick={handleSaveForecast}
                      >
                        Save forecast
                      </LoadingButton>
                    )}
                  </Box>
                  {productionForecast.intervals.some(
                    (interval) => interval.hasLimitations,
                  ) && (
                    <Tooltip title="Active limitation">
                      <WarningAmberIcon color="error" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                  {productionForecast.intervals.some(
                    (interval: any) => interval.hasAdjustments,
                  ) && (
                    <Tooltip title="Forecast adjusted by external partner">
                      <PublishedWithChangesIcon
                        color="info"
                        sx={{ ml: 1 }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </th>
            </tr>
            <tr>
              {preferredOrder.map((type) => {
                if (availableForecastTypes.includes(type)) {
                  return (
                    <th key={"Type-" + type}>
                      {forecastTypes[type as keyof typeof forecastTypes]}
                    </th>
                  );
                }
              })}
              <th>Correction</th>
              <th>Forecast</th>
            </tr>
            <tr>
              {preferredOrder.map((type) => {
                if (availableForecastTypes.includes(type)) {
                  return (
                    <th key={"WeightType-" + type}>
                      {type in weights && (
                        <input
                          type="number"
                          className={classNames(
                            styles.weightInput,
                            weightError && styles.weightsError,
                            styles[weights[type as keyof typeof weights].type],
                          )}
                          value={weights[type as keyof typeof weights].value}
                          onChange={(e) =>
                            handleSetWeight(type, e.target.value)
                          }
                        ></input>
                      )}
                    </th>
                  );
                }
              })}
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {productionForecast &&
              periodStarts.map((period, rowIndex) => (
                <tr key={"Row-" + rowIndex}>
                  {preferredOrder.map((type) => {
                    const interval = productionForecast.intervals[rowIndex];

                    const value = interval.values.find((v) => v.type === type);

                    // Determine provider type and check for provider-specific limitations
                    const providerType = getProviderType(
                      type,
                      productionParkType,
                    );
                    const hasProviderLimitation =
                      (providerType === "solar" &&
                        ((interval.activeMaxOutputLimitationSun !== undefined &&
                          interval.activeMaxOutputLimitationSun !== null) ||
                          (interval.activeAvailabilityLimitationSun !==
                            undefined &&
                            interval.activeAvailabilityLimitationSun !==
                              null))) ||
                      (providerType === "wind" &&
                        ((interval.activeMaxOutputLimitationWind !==
                          undefined &&
                          interval.activeMaxOutputLimitationWind !== null) ||
                          (interval.activeAvailabilityLimitationWind !==
                            undefined &&
                            interval.activeAvailabilityLimitationWind !==
                              null)));

                    if (availableForecastTypes.includes(type)) {
                      return (
                        <td
                          key={type + " " + rowIndex}
                          className={classNames(
                            styles.data,
                            tableMatrix[rowIndex]?.values.find(
                              (v) => v.type === type,
                            )?.isSelected && styles.selected,
                            hasProviderLimitation && styles.activeLimitation,
                          )}
                          onMouseDown={() => {
                            if (value) {
                              handleCellClick(rowIndex, type);
                            }
                          }}
                          onMouseEnter={() => {
                            if (value && isMouseDown) {
                              handleCellClick(rowIndex, type);
                            }
                          }}
                        >
                          <span className={styles.cellContent}>
                            <span
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {value
                                ? formatNumberByThousands(
                                    Math.round(value?.value),
                                  )
                                : "-"}
                            </span>
                            {value?.type === "FORECA" && (
                              <span className={styles.forecaSymbol}>
                                <img
                                  src={
                                    forecaSymbols[
                                      value.forecaSymbol as keyof typeof forecaSymbols
                                    ]
                                  }
                                  alt={""}
                                />
                              </span>
                            )}
                            {hasProviderLimitation && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography component="p" fontSize="14px">
                                      Output limitation (
                                      {providerType.toUpperCase()})
                                    </Typography>
                                    <Typography component="p" fontSize="12px">
                                      {"Original value: " +
                                        value?.originalValue}
                                    </Typography>
                                    {providerType === "solar" &&
                                      interval.activeMaxOutputLimitationSun !==
                                        undefined &&
                                      interval.activeMaxOutputLimitationSun !==
                                        null && (
                                        <Typography fontSize="12px">
                                          {"Max output: " +
                                            interval.activeMaxOutputLimitationSun +
                                            " kW"}
                                        </Typography>
                                      )}
                                    {providerType === "wind" &&
                                      interval.activeMaxOutputLimitationWind !==
                                        undefined &&
                                      interval.activeMaxOutputLimitationWind !==
                                        null && (
                                        <Typography fontSize="12px">
                                          {"Max output: " +
                                            interval.activeMaxOutputLimitationWind +
                                            " kW"}
                                        </Typography>
                                      )}
                                    {providerType === "solar" &&
                                      interval.activeAvailabilityLimitationSun !==
                                        undefined &&
                                      interval.activeAvailabilityLimitationSun !==
                                        null && (
                                        <Typography fontSize="12px">
                                          {"Output availability: " +
                                            interval.activeAvailabilityLimitationSun *
                                              100 +
                                            "%"}
                                        </Typography>
                                      )}
                                    {providerType === "wind" &&
                                      interval.activeAvailabilityLimitationWind !==
                                        undefined &&
                                      interval.activeAvailabilityLimitationWind !==
                                        null && (
                                        <Typography fontSize="12px">
                                          {"Output availability: " +
                                            interval.activeAvailabilityLimitationWind *
                                              100 +
                                            "%"}
                                        </Typography>
                                      )}
                                  </Box>
                                }
                                placement="top"
                              >
                                <WarningAmberIcon
                                  sx={{
                                    marginLeft: "4px",
                                    width: "16px",
                                    height: "16px",
                                  }}
                                  color="error"
                                />
                              </Tooltip>
                            )}
                          </span>
                        </td>
                      );
                    }
                  })}
                  <td className={`${styles.correction}`}>
                    <input
                      type="number"
                      className={styles.correctionInput}
                      value={tableMatrix[rowIndex]?.correction}
                      onChange={(e) =>
                        handleSetCorrection(rowIndex, e.target.value)
                      }
                      step="1"
                    />
                  </td>
                  <td
                    key="forecast"
                    className={classNames(
                      styles.data,
                      productionForecast.intervals[rowIndex]?.hasAdjustments &&
                        styles.adjustedForecast,
                    )}
                  >
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {tableMatrix[rowIndex]
                        ? formatNumberByThousands(
                            Math.round(tableMatrix[rowIndex]?.forecast),
                          )
                        : 0}
                      {productionForecast.intervals[rowIndex]
                        ?.hasAdjustments && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography component="p" fontSize="14px">
                                Forecast adjusted by external partner
                              </Typography>
                              {(() => {
                                const forecastEntry = productionForecast.intervals[
                                  rowIndex
                                ]?.values.find(
                                  (v: any) => v.type === "FORECAST",
                                );
                                if (!forecastEntry) return null;
                                return (
                                  <>
                                    {forecastEntry.originalValue !==
                                      undefined &&
                                      forecastEntry.originalValue !== null && (
                                        <Typography
                                          component="p"
                                          fontSize="12px"
                                        >
                                          {"Original value: " +
                                            forecastEntry.originalValue}
                                        </Typography>
                                      )}
                                    {forecastEntry.adjustedValue !==
                                      undefined &&
                                      forecastEntry.adjustedValue !== null && (
                                        <Typography
                                          component="p"
                                          fontSize="12px"
                                        >
                                          {"Adjusted value: " +
                                            forecastEntry.adjustedValue}
                                        </Typography>
                                      )}
                                  </>
                                );
                              })()}
                            </Box>
                          }
                          placement="top"
                        >
                          <PublishedWithChangesIcon
                            sx={{
                              marginLeft: "4px",
                              width: "16px",
                              height: "16px",
                            }}
                            color="info"
                          />
                        </Tooltip>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default React.memo(ProductionForecastTable);
